<template>
  <div class="action-tool">
    <div v-if="state === 'call' && onApprove" class="approval">
      <p class="label">{{ $t('aiAssistant.permissionRequired') }}</p>
      <p class="title">{{ summary.title }}</p>

      <div v-for="(section, si) in summary.sections" :key="si" class="section">
        <p class="section-label">{{ section.label }}</p>
        <ul class="section-lines">
          <li v-for="(line, li) in section.lines" :key="li">{{ line }}</li>
        </ul>
      </div>

      <details class="raw-toggle">
        <summary>{{ $t('aiAssistant.showTechnicalDetails') }}</summary>
        <pre class="args">{{ prettyArgs }}</pre>
      </details>

      <div class="actions">
        <button class="approve" @click="onApprove">
          {{ $t('aiAssistant.approve') }}
        </button>
        <button
          v-if="onApproveAndTrust && !isAlreadyTrusted"
          class="approve-all"
          :title="$t('aiAssistant.approveAllHint')"
          @click="onApproveAndTrust"
        >
          {{ $t('aiAssistant.approveAll') }}
        </button>
        <button class="reject" @click="onReject">
          {{ $t('aiAssistant.reject') }}
        </button>
      </div>
      <p v-if="isAlreadyTrusted" class="trusted-note">
        {{ $t('aiAssistant.trustedForSession') }}
      </p>
    </div>
    <div v-else-if="state === 'call'" class="pending">
      <span class="pending-dot"></span>
      <span>{{ summary.title }}…</span>
    </div>
    <div v-else-if="state === 'result'" class="result success">
      <p>{{ $t('aiAssistant.done') }}: {{ summary.title }}</p>
    </div>
    <div v-else-if="state === 'error'" class="result error">
      <p>{{ $t('aiAssistant.failed') }}: {{ summary.title }}</p>
      <pre v-if="errorDetail">{{ errorDetail }}</pre>
    </div>
  </div>
</template>

<script>
import { summarizeCall } from "./summarize";

export default {
  name: "ActionToolApproval",
  props: {
    toolName: { type: String, required: true },
    args: { type: Object, required: true },
    state: { type: String, required: true },
    result: { type: [Object, String], default: null },
    onApprove: { type: Function, default: null },
    onApproveAndTrust: { type: Function, default: null },
    onReject: { type: Function, default: null },
    isAlreadyTrusted: { type: Boolean, default: false },
  },
  computed: {
    summary() {
      try {
        return summarizeCall(this.toolName, this.args);
      } catch {
        return {
          title: this.toolName.replace(/^action_/, "").replace(/_/g, " "),
          sections: [],
        };
      }
    },
    prettyArgs() {
      try {
        return JSON.stringify(this.args, null, 2);
      } catch {
        return String(this.args);
      }
    },
    errorDetail() {
      if (!this.result) return "";
      try {
        const content = this.result?.content ?? [];
        const first = content[0];
        if (first?.text) return first.text;
        return typeof this.result === "string"
          ? this.result
          : JSON.stringify(this.result, null, 2);
      } catch {
        return "";
      }
    },
  },
};
</script>

<style scoped>
.action-tool {
  margin: 8px 0;
}
.approval {
  border: 1px solid #f59e0b;
  background: #fffbeb;
  padding: 12px;
  border-radius: 8px;
}
.label {
  font-weight: 600;
  margin: 0 0 6px;
  color: #92400e;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.title {
  margin: 0 0 10px;
  color: #111827;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.3;
}
.section {
  margin: 8px 0;
}
.section-label {
  margin: 0 0 2px;
  font-size: 11px;
  color: #78350f;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.section-lines {
  margin: 0;
  padding-inline-start: 16px;
  font-size: 13px;
  color: #1f2937;
  line-height: 1.45;
}
.section-lines li {
  margin: 2px 0;
  white-space: pre-wrap;
}
.raw-toggle {
  margin: 10px 0 8px;
  font-size: 11px;
  color: #6b7280;
  cursor: pointer;
}
.raw-toggle summary {
  outline: none;
  user-select: none;
}
.args {
  font-size: 11px;
  background: #fff;
  padding: 8px;
  border-radius: 4px;
  overflow-x: auto;
  margin: 6px 0 0;
  white-space: pre-wrap;
  border: 1px solid #fde68a;
  color: #1f2937;
}
.actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
}
.approve,
.approve-all,
.reject {
  padding: 7px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
}
.approve {
  background: #10b981;
  color: white;
}
.approve:hover {
  background: #059669;
}
.approve-all {
  background: #047857;
  color: white;
  border: 1px dashed #34d399;
}
.approve-all:hover {
  background: #065f46;
}
.reject {
  background: white;
  color: #dc2626;
  border: 1px solid #dc2626;
}
.reject:hover {
  background: #fef2f2;
}
.trusted-note {
  margin: 8px 0 0;
  font-size: 11px;
  color: #065f46;
  font-style: italic;
}
.pending {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
  padding: 6px 10px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px dashed #e5e7eb;
}
.pending-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #6366f1;
  animation: pulse 1.4s infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}
.result {
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}
.result.success {
  background: #d1fae5;
  color: #065f46;
}
.result.error {
  background: #fee2e2;
  color: #991b1b;
}
.result p {
  margin: 0;
}
.result pre {
  margin: 8px 0 0;
  padding: 8px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  font-size: 11px;
  white-space: pre-wrap;
  overflow-x: auto;
}
</style>
